import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormsModule,
	ReactiveFormsModule,
	UntypedFormControl,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
} from '@angular/material/dialog';
import {
	AssignableRelationshipType,
	AssignableRelationshipTypeEnum,
	ControlAssignment,
	ControlAssignmentOption,
	DeviceControl,
	emptyDeviceAssignment,
	emptyDeviceControl,
} from 'src/app/models/data/deviceControl';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import {
	Device,
	DeviceAccessSource,
	DeviceGroup,
	hasOnlyGen3WallMountDevices,
	hasOnlyPdqDevices,
	hasOnlySpecialGrantAccessDevices,
	hasSentriVaultDevices,
} from 'src/app/models/data/device';
import { User } from 'src/app/models/data/user';
import { Company } from 'src/app/models/data/company';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerInputEvent,
	MatDatepickerToggle,
	MatDatepickerToggleIcon,
} from '@angular/material/datepicker';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { SystemCapabilitiesIconLegendComponent } from 'src/app/components/system-capabilities-icon-legend/system-capabilities-icon-legend.component';
import { UserProfile } from 'src/app/models/model';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import { MOBILE_BREAKPOINT } from 'src/app/app.constants';
import { DeviceControlDetailsCommonComponent } from '../../access/device-control-details-common/device-control-details-common.component';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NotAuthorizedNoticeComponent } from '../../../components/not-authorized-notice/not-authorized-notice.component';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { NgTemplateOutlet } from '@angular/common';
import { MatDivider } from '@angular/material/list';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
	AccessDatePlaceholderPipe,
	DeviceAccessControlDurationPipe,
	DeviceControlDaysPipe,
} from '../../../components/pipes';

type ControlAssignmentWithOptions = ControlAssignmentOption & {
	allowSubAuthorization?: boolean;
	entityRelationshipId: string;
};

type DeviceGroupWithAllowSubAuth = DeviceGroup & {
	allAllowSubAuthorization?: boolean;
};
export type DeviceAssignments = {
	allowCodeGeneration: {
		value?: boolean;
		disabled?: boolean;
	};
	allowHaspCodeGeneration: {
		value?: boolean;
		disabled?: boolean;
	};
};

type ControlResponse = {
	control: Components.Schemas.DeviceControl;
	assignments: Components.Schemas.ControlAssignment[];
	devices: Components.Schemas.Device[];
};
type DeviceGroupOption = DeviceGroup & { displayName?: string };

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-assign-device-controls',
	templateUrl: './assign-device-controls.component.html',
	styleUrls: [
		'./assign-device-controls.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		NotAuthorizedNoticeComponent,
		MatDialogContent,
		HasSystemCapabilityDirective,
		MatGridList,
		MatGridTile,
		NgTemplateOutlet,
		MatDivider,
		LoadingIndicatorComponent,
		DeviceControlDetailsCommonComponent,
		MultiSelectTypeaheadComponent,
		MatFormField,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		ReactiveFormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepickerToggleIcon,
		MatDatepicker,
		MatTooltip,
		MatSelect,
		MatOption,
		MatDialogActions,
		MatProgressSpinner,
		DeviceControlDaysPipe,
		DeviceAccessControlDurationPipe,
		AccessDatePlaceholderPipe,
	],
})
export class AssignDeviceControlsComponent
	implements AfterViewInit, OnInit, OnDestroy
{
	edits: {
		control: DeviceControl;
		deviceGroups: DeviceGroup[];
		userAssignments: User[];
		companyAssignments: Company[];
		isOwner: boolean;
		utcStartDate?: string;
		utcEndDate?: string;
	} = {
		control: emptyDeviceControl(),
		deviceGroups: [],
		userAssignments: [],
		companyAssignments: [],
		isOwner: true,
	};

	deviceAssignmentEdits: DeviceAssignments = emptyDeviceAssignment();

	readonly assignmentType!: 'user' | 'company';
	separatorKeysCodes: number[] = [ENTER, COMMA];
	selectable = true;
	removable = true;

	controlOptions?: DeviceControl[];
	selectedControl?: DeviceControl;
	dcInputHasFocus = false;
	showCodeGenerationOption = true; // setting this default to true so if company owns the device - this is true

	initiallySelectedDeviceGroupIds: string[] = [];

	deviceControlInput = new UntypedFormControl();
	filteredControlOptions: Observable<DeviceControl[]> = new Observable<
		DeviceControl[]
	>();

	controlAssignment?: ControlAssignmentOption[];

	searchCompanyCtrl = new UntypedFormControl();

	apiLoadingKeys = ApiLoadingKeys;
	activeCompanyId? = '';

	@ViewChild('searchCompanyInput')
	searchCompanyInput!: ElementRef<HTMLInputElement>;
	@ViewChild('searchCompanyInput', { read: MatAutocompleteTrigger })
	companyAutoTrigger!: MatAutocompleteTrigger;
	@ViewChild(DeviceControlDetailsCommonComponent)
	deviceControlDetailsCommonComponent!: DeviceControlDetailsCommonComponent;

	@ViewChildren(MultiSelectTypeaheadComponent)
	multiSelectTypeaheads!: QueryList<MultiSelectTypeaheadComponent<any>>;

	deviceGroupOptions?: DeviceGroupOption[];

	localizedText = {
		control: $localize`:@@assignDeviceControls-control:Search Device Controls`,
		deviceGroup: $localize`:@@assignDeviceControls-deviceGroup:Search Device Group Selection`,
		deviceGroupMessage: $localize`:@@assignDeviceControls-deviceGroupMessage:Not all devices in this group can be sub authorized.`,
		user: $localize`:@@assignDeviceControls-searchUser:Search User`,
		company: $localize`:@@assignDeviceControls-searchCompany:Search Company`,
		companySuboptionText: $localize`:@@assignDeviceControls-companySuboptionText:Allow Sub-Authorization`,
		companySuboptionTooltip: $localize`:@@assignDeviceControls-companySuboptionTooltip:Enable this company to grant device access to other companies.`,
		companySuboptionText2: $localize`:@@assignDeviceControls-companySuboptionText2:Allow Code Generation`,
		startDate: $localize`:@@assignDeviceControl-startDateHeader:Start Date`,
		endDate: $localize`:@@assignDeviceControl-endDateHeader:End Date`,
		optional: $localize`:@@assignDeviceControl-optional:Optional`,
	};

	isHaspCodeGenFeatureEnabled: boolean = false;

	get selectedDeviceGroups(): DeviceGroupWithAllowSubAuth[] {
		return this.deviceGroupList;
	}

	get selectedAssignmentOptions(): ControlAssignmentWithOptions[] {
		return [...this.userList, ...this.companyList];
	}

	startDate?: Date;
	endDate?: Date;
	minStartDate?: Date;
	maxStartDate?: Date;
	minEndDate?: Date;
	maxEndDate?: Date;

	userList: ControlAssignmentWithOptions[] = [];
	companyList: ControlAssignmentWithOptions[] = [];
	deviceGroupList: DeviceGroupWithAllowSubAuth[] = [];
	readonly = true;
	hasOwnedDevices = false;

	hideDeviceGroupSelection = false;
	hideCompanyUserSelection = false;

	startDateTimeControl = new FormControl('', [
		Validators.required,
		this.dateBoundaryValidator.bind(this),
	]);
	endDateTimeControl = new FormControl('', [
		this.dateBoundaryValidator.bind(this),
	]);
	subauthorizationControl = new FormControl<boolean | null>(null, [
		this.booleanValidator,
	]); // subauthorization dropdown control
	selectedControlAssignment?: ControlAssignment;

	// Track previous subauthorization value for confirmation dialog
	private previousSubauthorizationValue: boolean | null = null;
	// Track original subauthorization value when form is first populated
	private originalSubauthorizationValue: boolean | null = null;

	// Custom validator for boolean values only, currently used for sub-authorization dropdown
	private booleanValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (typeof value === 'boolean') {
			return null;
		}
		return { invalidBoolean: { value: value } };
	}

	// Custom validator for date boundaries
	private dateBoundaryValidator(
		control: AbstractControl
	): ValidationErrors | null {
		const isStartDate = control === this.startDateTimeControl;
		const value = control.value;
		if (!value && isStartDate) {
			return null; // Let required validator handle empty values
		}

		const date = new Date(value);

		// Normalize dates for comparison by setting time to 00:00:00.000
		const normalizeDate = (dateToNormalize: Date): Date => {
			const normalized = new Date(dateToNormalize);
			normalized.setHours(0, 0, 0, 0);
			return normalized;
		};

		const normalizedInputDate = normalizeDate(date);

		if (isStartDate) {
			// In edit mode, don't enforce today's date as minimum
			if (
				!this.isEditMode &&
				this.minStartDate &&
				normalizedInputDate < normalizeDate(this.minStartDate)
			) {
				return {
					dateTooEarly: { minDate: this.minStartDate, actualDate: date },
				};
			}
			if (
				this.maxStartDate &&
				normalizedInputDate > normalizeDate(this.maxStartDate)
			) {
				return {
					dateTooLate: { maxDate: this.maxStartDate, actualDate: date },
				};
			}
		} else {
			// If end date is undefined and max date is not the default, mark as invalid
			if (
				!value &&
				this.maxEndDate &&
				normalizeDate(this.maxEndDate).getTime() !==
					normalizeDate(new Date('12/31/2500')).getTime()
			) {
				return {
					endDateRequired: {
						message:
							'End date is required when there is a maximum date constraint',
					},
				};
			}

			if (value) {
				// End date validation
				// In edit mode, don't enforce today's date as minimum
				if (
					!this.isEditMode &&
					this.minEndDate &&
					normalizedInputDate < normalizeDate(this.minEndDate)
				) {
					return {
						dateTooEarly: { minDate: this.minEndDate, actualDate: date },
					};
				}
				if (
					this.maxEndDate &&
					normalizedInputDate > normalizeDate(this.maxEndDate)
				) {
					return {
						dateTooLate: { maxDate: this.maxEndDate, actualDate: date },
					};
				}
			}
		}

		return null;
	}

	private hasAtLeastOneDeviceCapability(): boolean {
		return (
			this.edits.control.permissions.canInstallDevice.value ||
			this.getDeviceAccessGranted() ||
			this.edits.control.permissions.canRemoveDevice.value ||
			this.deviceAssignmentEdits.allowCodeGeneration.value ||
			this.getEffectiveAllowHaspCodeGeneration() ||
			(this.edits.control.permissions.canArmDisarmAjaxAlarm as any)?.value ||
			(this.edits.control.permissions.canOverrideAjaxAlarm as any)?.value ||
			(this.hasOnlySpecialGrantAccessDevices &&
				!this.deviceControlDetailsCommonComponent.showDeviceCapabilitiesSection)
		);
	}

	/** Hasp code gen is unavailable for Gen3-only wall mounts. */
	private getEffectiveAllowHaspCodeGeneration(): boolean {
		if (this.hasOnlyGen3WallMountDevices) {
			return false;
		}
		return !!this.deviceAssignmentEdits.allowHaspCodeGeneration.value;
	}

	/** Device Access is granted when either coupled permission flag is set and editable. */
	private getDeviceAccessGranted(): boolean {
		const openDoor = this.edits.control.permissions.canOpenKeyDoor;
		const releaseHasp = this.edits.control.permissions.canReleaseHasp;
		return (
			(!openDoor.disabled && !!openDoor.value) ||
			(!releaseHasp.disabled && !!releaseHasp.value)
		);
	}

	private requiresDeviceCapabilitySelection(): boolean {
		const standardCapabilitiesVisible = !this.hasOnlySpecialGrantAccessDevices;
		const ajaxCapabilitiesVisible =
			this.hasAtLeastOneAJAXDevice &&
			!this.edits.control.permissions.canArmDisarmAjaxAlarm.disabled;
		const specialCompanyCapabilitiesVisible =
			this.hasOnlySpecialGrantAccessDevices &&
			this.assignmentType === AssignableRelationshipTypeEnum.company;

		return (
			standardCapabilitiesVisible ||
			ajaxCapabilitiesVisible ||
			specialCompanyCapabilitiesVisible
		);
	}

	// Sub-authorization dropdown options, defaults to null value and shows placeholder
	// if selecting multiple companies
	subauthorizationOptions = [
		{ value: true, label: 'Yes', i18n: '@@assignDeviceControl-yesOption' },
		{ value: false, label: 'No', i18n: '@@assignDeviceControl-noOption' },
	];

	deviceAccessTooltip = $localize`:@@assignDeviceControls-deviceAccessTooltip:Device Access`;
	installDeviceTooltip = $localize`:@@assignDeviceControls-installDeviceTooltip:Install Device`;
	removeDeviceTooltip = $localize`:@@assignDeviceControls-removeDeviceTooltip:Remove Device`;
	accessCodeTooltip = $localize`:@@assignDeviceControls-accessCodeTooltip:Generate Access Code`;
	haspCodeTooltip = $localize`:@@assignDeviceControls-haspCodeTooltip:Generate Hasp Code`;
	armDisarmAlarmTooltip = $localize`:@@assignDeviceControls-armDisarmAlarmTooltip:Arm/Disarm Alarm`;
	alarmOverrideTooltip = $localize`:@@assignDeviceControls-alarmOverrideTooltip:Alarm Override`;

	companyId?: string;
	devices: any[] = [];
	@Input() fetchingDeviceControlSummaries = false;

	@Input() resultingDeviceAccess?: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}[] = undefined;
	clonedDeviceControlsJSON = '';
	clonedDeviceControls: DeviceControl[] = [];

	hasOnlyPDQDevices: boolean = true; // true if a device is PDQ or device group only has PDQ (lock set) devices
	hasAtLeastOneAJAXDevice: boolean = false;
	hasSentriVaultDevices: boolean = false; // true if a device is SentriVault or device group only has SentriVault devices
	/** True for Senturian/PDQ-only, SentriVault-only, or mixed of only those types. */
	hasOnlySpecialGrantAccessDevices: boolean = false;
	hasOnlyGen3WallMountDevices: boolean = false;
	private dgcontrols: Map<string, ControlResponse[]> = new Map<
		string,
		ControlResponse[]
	>();

	defaultGridRowHeight = '52px';
	gridRowHeight = this.defaultGridRowHeight;
	private destroy$ = new Subject<void>();
	isMobile = false;

	isEditMode: boolean = false;
	isSaving = false;

	// New properties for multi-step edit mode
	showAccessGroupSelectionStep = false;
	selectedAccessGroup: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	} | null = null;
	accessGroups: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}[] = [];
	affectedAssignments: { childEntityName: string }[] = [];
	hadMultipleAccessGroups = false;
	// Store original assignments to preserve duplicates between editing on the first and second steps
	originalControlAssignments: ControlAssignment[] = [];

	minStartTime: string = '00:00:00';
	maxEndTime: string = '00:00:00';

	descendantCompanies: any[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			deviceGroupId?: string;
			deviceId?: string;
			control?: DeviceControl;
			assignmentType: AssignableRelationshipTypeEnum;
			devices: {
				device: Device;
				groupNames?: string[];
				source?: DeviceAccessSource;
			}[];
			companyId: string;
			hasAjaxDevice?: boolean;
			hasOnlyPDQ?: boolean;
			hasSentriVaultDevices?: boolean;
			hasOnlySpecialGrantAccessDevices?: boolean;
			// Edit mode properties
			isEditMode?: boolean;
			controlAssignments?: ControlAssignment[];
			showCompanyMessage?: boolean;
		},
		private readonly dialogRef: MatDialogRef<AssignDeviceControlsComponent>,
		private readonly apiService: ApiService,
		private readonly banner: BannerMessageService,
		private readonly scService: SystemCapabilityService,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
		private breakpointObserver: BreakpointObserver
	) {
		// Set default date values based on edit mode
		if (data.isEditMode) {
			// In edit mode, allow past dates by setting very early minimum dates
			this.minStartDate = new Date(1900, 0, 1);
			this.minEndDate = new Date(1900, 0, 1);
		} else {
			// In create mode, set today's date as minimum
			const todaysDate = new Date();
			todaysDate?.setDate(todaysDate?.getDate() + 1);
			this.minEndDate = todaysDate;
		}

		this.assignmentType = data.assignmentType;
		const ownedDevices = data?.devices?.filter(
			(device) => device.source?.companyId === data?.companyId
		);
		this.hasOwnedDevices = ownedDevices?.length > 0;
		this.companyId = data.companyId;
		this.hideDeviceGroupSelection = !!data?.deviceId || !!data?.deviceGroupId;
		this.hideCompanyUserSelection = !!data.isEditMode;
		this.edits.control.startTime = '00:00:00';
		this.edits.control.endTime = '00:00:00';
		this.isEditMode = !!data.isEditMode;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	async ngOnInit(): Promise<void> {
		if (typeof this.data.hasOnlyPDQ === 'boolean') {
			this.hasOnlyPDQDevices = this.data.hasOnlyPDQ;
		} else {
			this.hasOnlyPDQDevices = hasOnlyPdqDevices(this.data?.devices);
		}
		if (typeof this.data.hasAjaxDevice === 'boolean') {
			this.hasAtLeastOneAJAXDevice = this.data.hasAjaxDevice;
		} else {
			this.hasAtLeastOneAJAXDevice = !!this.data?.devices.find(
				(d) => !!d.device.ajaxEnabled
			);
		}

		if (typeof this.data.hasSentriVaultDevices === 'boolean') {
			this.hasSentriVaultDevices = this.data.hasSentriVaultDevices;
		} else {
			this.hasSentriVaultDevices = hasSentriVaultDevices(this.data?.devices);
		}

		if (typeof this.data.hasOnlySpecialGrantAccessDevices === 'boolean') {
			this.hasOnlySpecialGrantAccessDevices =
				this.data.hasOnlySpecialGrantAccessDevices;
		} else {
			this.hasOnlySpecialGrantAccessDevices = hasOnlySpecialGrantAccessDevices(
				this.data?.devices ?? []
			);
		}

		this.hasOnlyGen3WallMountDevices = hasOnlyGen3WallMountDevices(
			this.data?.devices ?? []
		);
		if (this.hasOnlyGen3WallMountDevices) {
			this.deviceAssignmentEdits.allowHaspCodeGeneration.value = false;
		}

		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;

		this.breakpointObserver
			.observe([`(max-width: ${MOBILE_BREAKPOINT}px)`])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.gridRowHeight = result.matches
					? '76px'
					: this.defaultGridRowHeight;
				this.isMobile = result.matches;
				this.cdr.detectChanges(); // to properly update the gridRowHeight
			});

		// Subscribe to subauthorization control changes
		this.subauthorizationControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((newValue) => {
				this.onSubauthorizationValueChanged(newValue);
			});
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.getUserProfileData();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		// populate the user dropdown
		this.controlAssignment = (
			await this.getControlAssignmentOptions(this.assignmentType)
		).map((company) => company);

		let queryParams: QueryParam[] = [];
		if (this.data.deviceGroupId) {
			// If we're opening the window from inside a specific Device Group we'll filter out all
			// of the other device groups and auto select the device group we're in (done below).
			queryParams = [
				{ param: 'deviceGroupId', value: this.data.deviceGroupId },
			];
		}

		// If we aren't opening the window from inside a specific Device Group we'll let the user select
		// the device group they want to use.
		// populate the device group dropdown
		this.deviceGroupOptions = (
			await this.apiService.getDeviceGroupOptions(queryParams)
		).map((item) => {
			const dg: DeviceGroupOption = item.group;
			dg.displayName = `${dg.name}`;
			if (item.source?.companyId !== this.companyId) {
				dg.displayName += ` (${item.source?.companyName})`;
			}
			return dg;
		});
		this.deviceGroupOptions = this.deviceGroupOptions?.filter(
			(item) => item.status === 'active'
		); // Only active DGs
		if (this.assignmentType === 'company') {
			this.deviceGroupOptions = this.deviceGroupOptions?.filter(
				(dg) => dg.canSubauthorize === 'all' || dg.canSubauthorize === 'partial'
			);
		}

		if (this.data.deviceGroupId || this.data.deviceId) {
			if (this.data.deviceGroupId) {
				// if deviceGroupId is passed in deviceGroupOptions is set with the deviceGroupId so the count should be 1.
				// this is added to check if the passed in deviceGroup is owned or inherited so the resultingDeviceAccesses can be shown accordingly
				if (
					this.deviceGroupOptions?.length === 1 &&
					this.deviceGroupOptions[0].companyId === this.activeCompanyId
				) {
					this.hasOwnedDevices = true;
				}
				this.initiallySelectedDeviceGroupIds = [this.data.deviceGroupId];
			}

			// Avoid duplicate calls to requestDeviceControlSummaries
			if (!this.data.isEditMode) {
				this.fetchingDeviceControlSummaries = true;
				this.requestDeviceControlSummaries();
			}
		}

		if (this.data.isEditMode) {
			// Store original control assignments to preserve original data for the edit step
			this.originalControlAssignments = [...this.data.controlAssignments!];

			// Check if we need to show the access group selection step
			await this.prepareAccessGroupSelectionStep();

			// If we're showing the access group selection step, don't proceed with normal form setup
			if (this.showAccessGroupSelectionStep) {
				this.cdr.detectChanges();
				return;
			} else {
				this.requestDeviceControlSummaries();
			}

			if (this.data.controlAssignments?.length === 1) {
				this.populateFormForEdit(this.data.controlAssignments[0]);
			}

			// Fetch descendant companies for the assignment's company (if available)
			if (
				this.data.controlAssignments &&
				this.data.controlAssignments.length > 0 &&
				this.assignmentType === 'company'
			) {
				try {
					this.descendantCompanies =
						await this.apiService.getDeviceControlDescendantCompanies(
							this.data.controlAssignments.map((assignment) => assignment.id)
						);
				} catch (e) {
					this.descendantCompanies = [];
				}
			}
		}

		// Change detection was set to "OnPush" for this component (not sure exactly why maybe there was some
		// performance related issue) so we have to run change detection manually when needed. Without this the
		// device group won't get selected automatically.
		this.cdr.detectChanges();
	}

	populateFormForEdit(assignment: ControlAssignment): void {
		// Pre-select device groups
		if (assignment.deviceGroup) {
			const deviceGroupOption = this.deviceGroupOptions?.find(
				(dg) => dg.id === assignment.deviceGroup?.id
			);
			if (deviceGroupOption) {
				this.deviceGroupList = [
					{
						...deviceGroupOption,
						allAllowSubAuthorization: assignment.allowSubAuthorization,
					},
				];
			}
		}

		if (assignment.utcStartDate) {
			this.startDate = new Date(assignment.utcStartDate);
			this.startDateTimeControl.setValue(assignment.utcStartDate);
			this.edits.utcStartDate = assignment.utcStartDate;
		}

		if (assignment.utcEndDate) {
			this.endDate = new Date(assignment.utcEndDate);
			this.endDateTimeControl.setValue(assignment.utcEndDate);
			this.edits.utcEndDate = assignment.utcEndDate;
		}

		// Set permissions and options
		this.deviceAssignmentEdits.allowCodeGeneration.value =
			assignment.allowCodeGeneration ?? true;
		this.deviceAssignmentEdits.allowHaspCodeGeneration.value = this
			.hasOnlyGen3WallMountDevices
			? false
			: (assignment.allowHaspCodeGeneration ?? true);
		this.showCodeGenerationOption = assignment.allowCodeGeneration ?? false;

		if (assignment.deviceControl) {
			this.selectedControl = assignment.deviceControl;
			this.edits.control = { ...assignment.deviceControl };

			if (assignment.deviceControl.days) {
				Object.keys(assignment.deviceControl.days).forEach((day) => {
					const dayKey = day as keyof typeof this.edits.control.days;
					if (
						this.edits.control.days[dayKey] &&
						assignment.deviceControl?.days[dayKey]
					) {
						this.edits.control.days[dayKey] = {
							...assignment.deviceControl.days[dayKey],
						};
					}
				});
			}

			if (assignment.deviceControl.permissions) {
				Object.keys(assignment.deviceControl.permissions).forEach(
					(permission) => {
						const permKey =
							permission as keyof typeof this.edits.control.permissions;
						if (
							this.edits.control.permissions[permKey] &&
							assignment.deviceControl?.permissions[permKey]
						) {
							this.edits.control.permissions[permKey] = {
								...assignment.deviceControl.permissions[permKey],
							};
						}
					}
				);
			}

			if (assignment.deviceControl.startTime) {
				this.edits.control.startTime = assignment.deviceControl.startTime;
			}
			if (assignment.deviceControl.endTime) {
				this.edits.control.endTime = assignment.deviceControl.endTime;
			}

			if (typeof assignment.allowSubAuthorization === 'boolean') {
				this.subauthorizationControl.setValue(assignment.allowSubAuthorization);
				this.previousSubauthorizationValue = assignment.allowSubAuthorization;
				this.originalSubauthorizationValue = assignment.allowSubAuthorization;
			}
		}

		this.selectedControlAssignment = assignment;
		this.cdr.detectChanges();
	}

	async getUserProfileData(): Promise<UserProfile> {
		return await this.apiService.getUserProfile();
	}

	async getControlAssignmentOptions(
		assignmentType: AssignableRelationshipType
	): Promise<ControlAssignmentOption[]> {
		return this.apiService.getControlAssignmentOptions(assignmentType);
	}

	displayDeviceControlFn(dc: DeviceControl): string {
		return dc && dc.name ? dc.name : '';
	}

	onCloseClicked(): void {
		this.dialogRef.close();
	}

	onStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.startDate = event.value ?? undefined;
		if (this.startDate) {
			const newMinEndDate = new Date(this.startDate);
			newMinEndDate.setDate(newMinEndDate.getDate() + 1);
			// In edit mode, don't enforce today's date as minimum for end date
			if (!this.isEditMode) {
				this.minEndDate = newMinEndDate;
			} else {
				// In edit mode, only set minEndDate if it's not already set or if the new value is later
				if (!this.minEndDate || newMinEndDate > this.minEndDate) {
					this.minEndDate = newMinEndDate;
				}
			}

			// Only reset end date if it's before the new minimum end date
			if (this.endDate && this.endDate < this.minEndDate) {
				this.endDate = undefined;
				this.endDateTimeControl.reset();
			}
		}
		// Trigger validation for both controls since start date change affects end date validation
		this.startDateTimeControl.updateValueAndValidity({
			onlySelf: true,
			emitEvent: true,
		});
		this.endDateTimeControl.updateValueAndValidity({
			onlySelf: true,
			emitEvent: true,
		});
	}

	onEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.endDate = event.value ?? undefined;
		this.endDateTimeControl.updateValueAndValidity({
			onlySelf: true,
			emitEvent: true,
		});
	}

	isTimeValid(): boolean {
		// Always check if start time is greater than end time
		if (
			this.edits.control.startTime >= this.edits.control.endTime &&
			this.edits.control.endTime !== '24:00:00'
		) {
			return false;
		}

		// For non-owner controls, check min/max boundaries
		if (!this.hasOwnedDevices && this.selectedControlAssignment) {
			const minTime = this.minStartTime ?? '00:00:00';
			const maxTime = this.maxEndTime ?? '24:00:00';
			if (
				this.edits.control.startTime < minTime ||
				this.edits.control.startTime > maxTime
			) {
				return false;
			}
			if (
				this.edits.control.endTime < minTime ||
				this.edits.control.endTime > maxTime
			) {
				return false;
			}
		}

		return true;
	}

	areDatesValid(): boolean {
		// Check if start date is before end date (if end date exists)
		if (this.startDate && this.endDate && this.startDate >= this.endDate) {
			return false;
		}

		// For inherited controls, check if dates are within boundaries
		if (!this.hasOwnedDevices) {
			if (!this.selectedControlAssignment) {
				return false;
			}
			const inheritedStartDate = new Date(
				this.selectedControlAssignment.utcStartDate
			);
			const inheritedEndDate = this.selectedControlAssignment.utcEndDate
				? new Date(this.selectedControlAssignment.utcEndDate)
				: new Date('12/31/2500');

			// Check if start date is within inherited boundaries
			if (
				this.startDate &&
				(this.startDate < inheritedStartDate ||
					this.startDate > inheritedEndDate)
			) {
				return false;
			}

			// Check if end date is within inherited boundaries
			if (
				this.endDate &&
				(this.endDate < inheritedStartDate || this.endDate > inheritedEndDate)
			) {
				return false;
			}
		}

		return true;
	}

	canSave(): boolean {
		if (!this.scService.has(SystemCapabilityEnum.deviceManagement)) {
			return false;
		}
		if (!this.selectedAssignmentOptions?.length && !this.isEditMode) {
			return false;
		}
		if (
			!this.selectedDeviceGroups?.length &&
			!this.data.deviceId &&
			!this.data.deviceGroupId
		) {
			// we're also checking the deviceId because the user is now allowed to make assignments
			// directly on a device instead of always needing a device group. In the background we
			// will create a device group with just that one device in it.
			return false;
		}
		if (!this.startDate) {
			return false;
		}
		if (this.edits.control.endTime === '00:00:00') {
			return false;
		}
		if (!this.isTimeValid()) {
			return false;
		}
		if (!this.areDatesValid()) {
			return false;
		}
		if (
			this.isEditMode &&
			this.assignmentType === 'company' &&
			!this.subauthorizationControl.valid
		) {
			return false;
		}
		// Check if at least one visible device capability is selected
		if (
			this.requiresDeviceCapabilitySelection() &&
			!this.hasAtLeastOneDeviceCapability()
		) {
			return false;
		}
		if (
			!this.edits.control.days.monday.value &&
			!this.edits.control.days.tuesday.value &&
			!this.edits.control.days.wednesday.value &&
			!this.edits.control.days.thursday.value &&
			!this.edits.control.days.friday.value &&
			!this.edits.control.days.saturday.value &&
			!this.edits.control.days.sunday.value
		) {
			return false;
		}
		return true;
	}

	canSubAuthSave(): boolean {
		if (
			!this.hasOwnedDevices &&
			(!this.selectedAssignmentOptions?.length ||
				this.fetchingDeviceControlSummaries) &&
			!this.isEditMode
		) {
			return false;
		}
		if (
			!this.hasOwnedDevices &&
			!this.selectedDeviceGroups?.length &&
			!this.data.deviceId &&
			!this.data.deviceGroupId
		) {
			// we're also checking the deviceId because the user is now allowed to make assignments
			// directly on a device instead of always needing a device group. In the background we
			// will create a device group with just that one device in it.
			return false;
		}
		if (!this.startDate) {
			return false;
		}
		// if end date was set by Company A then endDate must be required when giving sub-authorization
		if (this.edits.utcEndDate && !this.endDate) {
			return false;
		}
		if (!this.edits.control.startTime) {
			return false;
		}
		if (
			!this.edits.control.endTime ||
			this.edits.control.endTime === '00:00:00'
		) {
			return false;
		}
		if (!this.isTimeValid()) {
			return false;
		}
		if (!this.areDatesValid()) {
			return false;
		}
		if (
			this.isEditMode &&
			this.assignmentType === 'company' &&
			!this.subauthorizationControl.valid
		) {
			return false;
		}
		// Check if at least one visible device capability is selected
		if (
			this.requiresDeviceCapabilitySelection() &&
			!this.hasAtLeastOneDeviceCapability()
		) {
			return false;
		}
		if (
			!this.edits.control.days.monday.value &&
			!this.edits.control.days.tuesday.value &&
			!this.edits.control.days.wednesday.value &&
			!this.edits.control.days.thursday.value &&
			!this.edits.control.days.friday.value &&
			!this.edits.control.days.saturday.value &&
			!this.edits.control.days.sunday.value
		) {
			return false;
		}
		return true;
	}

	private markRequiredTypeaheadsTouched(): void {
		this.multiSelectTypeaheads?.forEach((typeahead) =>
			typeahead.markAsTouched()
		);
		this.cdr.detectChanges();
	}

	async saveAssignments(): Promise<void> {
		if (!this.canSave()) {
			this.markRequiredTypeaheadsTouched();
			this.banner.sendMessage(
				$localize`:@@assignDeviceControl-validationError:Validation error assigning device controls! Please try again.`
			);
			return;
		}

		this.isSaving = true;

		if (this.isEditMode) {
			if (
				!this.data.controlAssignments ||
				this.data.controlAssignments.length === 0
			) {
				this.isSaving = false;
				return;
			}

			const editControlAssignment: Components.Schemas.EditControlAssignment = {
				deviceControl: {
					days: {
						sunday: this.edits.control.days.sunday.disabled
							? false
							: this.edits.control.days.sunday.value,
						monday: this.edits.control.days.monday.disabled
							? false
							: this.edits.control.days.monday.value,
						tuesday: this.edits.control.days.tuesday.disabled
							? false
							: this.edits.control.days.tuesday.value,
						wednesday: this.edits.control.days.wednesday.disabled
							? false
							: this.edits.control.days.wednesday.value,
						thursday: this.edits.control.days.thursday.disabled
							? false
							: this.edits.control.days.thursday.value,
						friday: this.edits.control.days.friday.disabled
							? false
							: this.edits.control.days.friday.value,
						saturday: this.edits.control.days.saturday.disabled
							? false
							: this.edits.control.days.saturday.value,
					},
					startTime: this.edits.control.startTime,
					endTime: this.edits.control.endTime,
					permissions: {
						canOpenKeyDoor: this.edits.control.permissions.canOpenKeyDoor
							.disabled
							? false
							: this.getDeviceAccessGranted(),
						canInstallDevice: this.edits.control.permissions.canInstallDevice
							.disabled
							? false
							: this.edits.control.permissions.canInstallDevice.value,
						canRemoveDevice: this.edits.control.permissions.canRemoveDevice
							.disabled
							? false
							: this.edits.control.permissions.canRemoveDevice.value,
						canReleaseHasp: this.edits.control.permissions.canReleaseHasp
							.disabled
							? false
							: this.getDeviceAccessGranted(),
						canUpdateFirmware: this.edits.control.permissions.canUpdateFirmware
							.disabled
							? false
							: this.edits.control.permissions.canUpdateFirmware.value,
						canArmDisarmAjaxAlarm: this.edits.control.permissions
							.canArmDisarmAjaxAlarm.disabled
							? false
							: this.edits.control.permissions.canArmDisarmAjaxAlarm.value,
						canOverrideAjaxAlarm: this.edits.control.permissions
							.canOverrideAjaxAlarm.disabled
							? false
							: this.edits.control.permissions.canOverrideAjaxAlarm.value,
					},
				},
				utcStartDate: this.startDate?.toISOString(),
				utcEndDate: this.endDate?.toISOString() || null,
				allowCodeGeneration: this.deviceAssignmentEdits.allowCodeGeneration
					.disabled
					? false
					: this.deviceAssignmentEdits.allowCodeGeneration.value,
				allowHaspCodeGeneration: this.deviceAssignmentEdits
					.allowHaspCodeGeneration.disabled
					? false
					: this.getEffectiveAllowHaspCodeGeneration(),
				allowSubAuthorization: this.subauthorizationControl.value ?? false,
			};

			try {
				await this.apiService.editControlAssignmentsFull(
					this.data.controlAssignments.map((assignment) => assignment.id),
					editControlAssignment
				);
				this.dialogRef.close(true);
			} catch (error) {
				this.isSaving = false;
				this.banner.sendMessage(
					$localize`:@@assignDeviceControl-errorUpdating:Error updating control assignment. Please try again.`
				);
			}
		} else {
			// make sure the control name isn't set as that was used to make the mat-grid-list-item look selected
			this.edits.control.name = '';
			const assignments: ControlAssignment[] = [];

			this.selectedAssignmentOptions.forEach((assignment) => {
				if (this.hideDeviceGroupSelection) {
					assignments.push({
						id: '',
						deviceControlId: '',
						deviceGroupId: this.data.deviceGroupId,
						deviceId: this.data.deviceId,
						entityRelationshipId: assignment.entityRelationshipId,
						// tslint:disable-next-line: no-non-null-assertion
						utcStartDate: this.startDate!.toISOString(),
						utcEndDate: this.endDate?.toISOString(),
						allowSubAuthorization: !!assignment.allowSubAuthorization,
						allowCodeGeneration:
							!!this.deviceAssignmentEdits.allowCodeGeneration.value,
						allowHaspCodeGeneration: this.getEffectiveAllowHaspCodeGeneration(),
						isSystemAssignment: false,
					});
				} else {
					this.selectedDeviceGroups?.forEach((group) => {
						assignments.push({
							id: '',
							deviceControlId: '',
							deviceGroupId: group.id,
							deviceId: this.data.deviceId,
							entityRelationshipId: assignment.entityRelationshipId,
							// tslint:disable-next-line: no-non-null-assertion
							utcStartDate: this.startDate!.toISOString(),
							utcEndDate: this.endDate?.toISOString(),
							allowSubAuthorization: !!assignment.allowSubAuthorization,
							allowCodeGeneration:
								!!this.deviceAssignmentEdits.allowCodeGeneration.value,
							allowHaspCodeGeneration:
								this.getEffectiveAllowHaspCodeGeneration(),
							isSystemAssignment: false,
						});
					});
				}
			});

			this.apiService
				.createDeviceControlsAndControlAssignments(
					this.edits.control,
					assignments,
					this.selectedControlAssignment?.id
				)
				.then(() => {
					this.banner.sendMessage(
						$localize`:@@assignDeviceControl-success:Successfully assigned device controls.`
					);
					this.dialogRef.close();
				})
				.catch((err) => {
					this.banner.sendMessage(
						$localize`:@@assignDeviceControl-error:Error assigning device controls! Please try again.`
					);
				});
		}

		this.isSaving = false;
	}

	formatAccessTime(time: string): string {
		return time.trim().substr(0, 5);
	}

	showCapabilitiesLegend(): void {
		this.dialog.open(SystemCapabilitiesIconLegendComponent);
	}

	onDeviceGroupChange(event: DeviceGroupWithAllowSubAuth[]): void {
		this.deviceGroupList = event;
		this.requestDeviceControlSummaries();
	}

	async requestDeviceControlSummaries(): Promise<void> {
		if (
			this.hasOwnedDevices ||
			(this.deviceGroupList.length === 0 &&
				!this.data.deviceGroupId &&
				!this.data.deviceId)
		) {
			this.resultingDeviceAccess = undefined;
			this.fetchingDeviceControlSummaries = false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			// In edit mode, don't enforce today's date as minimum
			if (!this.isEditMode) {
				this.minStartDate = today;
				this.minEndDate = today;
			} else {
				// In edit mode, allow past dates by setting very early minimum dates
				this.minStartDate = new Date('1900-01-01');
				this.minEndDate = new Date('1900-01-01');
			}
			this.maxStartDate = new Date('12/31/2500');
			this.maxEndDate = new Date('12/31/2500');
			return;
		}

		// if the company has only inherited devices then make all the controls disabled.
		// When the user selects on a desired Inherited Control, that is be populated
		// but this is needed to prevent user from selecting a days/time/permissions they did not inherit
		this.minStartDate = new Date('12/31/2500');
		this.maxStartDate = new Date('12/31/2500');
		this.minEndDate = new Date('12/31/2500');
		this.maxEndDate = new Date('12/31/2500');
		this.edits.control.startTime = '00:00:00';
		this.edits.control.endTime = '00:00:00';
		this.edits.control.days.monday.disabled = true;
		this.edits.control.days.tuesday.disabled = true;
		this.edits.control.days.wednesday.disabled = true;
		this.edits.control.days.thursday.disabled = true;
		this.edits.control.days.friday.disabled = true;
		this.edits.control.days.saturday.disabled = true;
		this.edits.control.days.sunday.disabled = true;
		this.edits.control.permissions.canInstallDevice.disabled = true;
		this.edits.control.permissions.canOpenKeyDoor.disabled = true;
		this.edits.control.permissions.canRemoveDevice.disabled = true;
		this.edits.control.permissions.canReleaseHasp.disabled = true;
		this.edits.control.permissions.canArmDisarmAjaxAlarm.disabled = true;
		this.edits.control.permissions.canOverrideAjaxAlarm.disabled = true;

		this.resultingDeviceAccess = [];
		let deviceControlSummary: ControlResponse[] | undefined;

		if (this.data.deviceGroupId) {
			// this will get called when this.data.deviceGroupId is passed in
			deviceControlSummary = this.dgcontrols.get(this.data.deviceGroupId);

			if (!deviceControlSummary || !deviceControlSummary?.length) {
				this.fetchingDeviceControlSummaries = true;
				deviceControlSummary =
					await this.getInheritedDeviceGroupControlWithEditModeCheck(
						this.data.deviceGroupId,
						'0'
					);
				this.dgcontrols.set(this.data.deviceGroupId, deviceControlSummary);
			}
		} else if (this.data.deviceId) {
			// do this if deviceId is passed in. A DeviceGroup can be greated with a singe device in it when on the device tab on UI
			deviceControlSummary = this.dgcontrols.get(this.data.deviceId);

			if (!deviceControlSummary || !deviceControlSummary?.length) {
				this.fetchingDeviceControlSummaries = true;
				deviceControlSummary =
					await this.getInheritedDeviceGroupControlWithEditModeCheck(
						this.data.deviceId,
						this.data.deviceId
					);
				this.dgcontrols.set(this.data.deviceId, deviceControlSummary);
			}
		} else {
			for (const deviceGroup of this.deviceGroupList) {
				let deviceControlSummary = this.dgcontrols.get(deviceGroup.id);
				if (!deviceControlSummary || !deviceControlSummary?.length) {
					this.fetchingDeviceControlSummaries = true;
					deviceControlSummary =
						await this.getInheritedDeviceGroupControlWithEditModeCheck(
							deviceGroup.id,
							'0'
						);
					this.dgcontrols.set(deviceGroup.id, deviceControlSummary);
				}
				const assignments = deviceControlSummary
					.map((dcs) => dcs.assignments)
					.reduce((aggregator, value) => aggregator.concat(value, []));

				deviceGroup.allAllowSubAuthorization =
					deviceGroup.canSubauthorize === 'all';
			}
		}

		if (!!deviceControlSummary) {
			// For edit mode with single company/user, directly use the deviceControlSummary
			// since it's already filtered appropriately by the API
			if (this.isEditMode && !this.checkIfMultipleAccessGroups()) {
				this.resultingDeviceAccess = deviceControlSummary;
			} else {
				// Original filtering logic for create mode or multiple assignments
				const queryParameters: QueryParam[] = [
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 20 },
					{ param: 'isParent', value: false }, // this will return relationshipts where the companyId is child
				];
				// get the relationships where the company is Child to and filter the keep the controls with direct inheritence
				const companyRelationships =
					await this.apiService.getCompanyRelationshipsByCompanyId(
						this.activeCompanyId ?? '',
						queryParameters
					);

				deviceControlSummary.forEach((item) => {
					item.assignments.forEach((val) => {
						// if a control was system generated, then it should show as inherited control when user is on device/grant user or company access
						// if a control was user generated, then it should show as inherited control when user is on device or device group/grant  user or company access
						// if allowSubAuthorization = false, they should be able to grant User Access but NOT Company Access
						if (
							companyRelationships.findIndex(
								(a) => a.entityRelationship.id === val.entityRelationshipId
							) > -1 &&
							((this.assignmentType === 'company' &&
								val.allowSubAuthorization) ||
								this.assignmentType !== 'company') &&
							((this.data.deviceGroupId && !item.control.isSystemGenerated) ||
								this.data.deviceId)
						) {
							this.resultingDeviceAccess?.push(item);
						}
					});
				});
			}
		}
		// map to get just the Device Controls and use JSON Stringify to do a deep clone
		// this object will be used to capture what controls were received from the server
		// in order to reset the control if a user made changes and went back to the same control
		this.clonedDeviceControlsJSON = JSON.stringify(
			[...this.resultingDeviceAccess].map((res) => res.control)
		);

		this.fetchingDeviceControlSummaries = false;
		this.cdr.detectChanges();

		// Select the first inherited control by default (if creating a new control) if any exist and none are selected
		if (
			this.resultingDeviceAccess.length > 0 &&
			!this.resultingDeviceAccess.some((res) => res.control.name === 'selected')
		) {
			if (!this.isEditMode) {
				this.onSelectedInheritorChangedEvent(this.resultingDeviceAccess[0]);
			} else {
				// If editing ONE existing control, OR editing multiple controls that are inherited from the same parent control
				// select that control that is currently inherited
				if (
					this.data.controlAssignments?.length === 1 ||
					this.resultingDeviceAccess.length === 1
				) {
					const previousInheritanceChain =
						this.data.controlAssignments?.[0]
							?.controlAssignmentInheritanceChain;
					if (previousInheritanceChain) {
						const controlId = this.getParentControlIdFromInheritanceChain(
							previousInheritanceChain
						);
						const matchingControl = this.resultingDeviceAccess.find(
							(res) => res.assignments[0].id === controlId
						);
						this.onSelectedInheritorChangedEvent(
							matchingControl ?? this.resultingDeviceAccess[0]
						);
					} else {
						// Just in case, if there is no inheritance chain, select the first control
						this.onSelectedInheritorChangedEvent(this.resultingDeviceAccess[0]);
					}
				}
			}
		}
	}

	formatControlName(deviceAccessControl: DeviceControl): string {
		return deviceAccessControl.companyName ?? '';
	}

	onSelectedInheritorChangedEvent(deviceAccess: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}): void {
		// Prevent unselecting: if already selected, do nothing (we want to make these like radio buttons)
		if (deviceAccess.control.name === 'selected') {
			return;
		}
		// convert into the object
		this.clonedDeviceControls = JSON.parse(this.clonedDeviceControlsJSON);
		// get from the clonedDeviceControls to show what was passed originally
		const originalControl =
			this.clonedDeviceControls?.filter(
				(res) => res.id === deviceAccess.control.id
			)[0] ?? emptyDeviceControl();
		// Create a deep copy of the control to avoid modifying the displayed version
		const selectedControl = JSON.parse(JSON.stringify(originalControl));

		// unselect the other inherited device controls but setting the control.name to 'notSelected'. This will update the class on html
		this.resultingDeviceAccess?.forEach((res) => {
			if (res.control !== deviceAccess.control) {
				res.control.name = 'notSelected';
			}
		});
		// toggle the selection of mat-grid-tile to fake selection and set background color
		deviceAccess.control.name = 'selected';
		selectedControl.name = 'selected';
		// selected inherited control so set the isOwner from DeviceControl to false
		deviceAccess.control.isOwner = false;
		selectedControl.isOwner = false;

		this.minStartTime = deviceAccess.control.startTime ?? '00:00:00';
		this.maxEndTime = deviceAccess.control.endTime ?? '00:00:00';

		this.edits.utcStartDate = deviceAccess.assignments[0].utcStartDate;
		this.edits.utcEndDate = deviceAccess.assignments[0].utcEndDate;

		// Handle device capabilities differently in edit mode
		if (!this.isEditMode) {
			// In create mode, set disabled states and values based on inheritance
			selectedControl.days.monday.disabled =
				!deviceAccess.control.days.monday.value;
			selectedControl.days.tuesday.disabled =
				!deviceAccess.control.days.tuesday.value;
			selectedControl.days.wednesday.disabled =
				!deviceAccess.control.days.wednesday.value;
			selectedControl.days.thursday.disabled =
				!deviceAccess.control.days.thursday.value;
			selectedControl.days.friday.disabled =
				!deviceAccess.control.days.friday.value;
			selectedControl.days.saturday.disabled =
				!deviceAccess.control.days.saturday.value;
			selectedControl.days.sunday.disabled =
				!deviceAccess.control.days.sunday.value;
			selectedControl.permissions.canInstallDevice.disabled =
				!deviceAccess.control.permissions.canInstallDevice.value;
			selectedControl.permissions.canOpenKeyDoor.disabled =
				!deviceAccess.control.permissions.canOpenKeyDoor.value;
			selectedControl.permissions.canRemoveDevice.disabled =
				!deviceAccess.control.permissions.canRemoveDevice.value;
			selectedControl.permissions.canReleaseHasp.disabled =
				!deviceAccess.control.permissions.canReleaseHasp.value;
			selectedControl.permissions.canArmDisarmAjaxAlarm.disabled =
				!deviceAccess.control.permissions.canArmDisarmAjaxAlarm.value;
			selectedControl.permissions.canOverrideAjaxAlarm.disabled =
				!deviceAccess.control.permissions.canOverrideAjaxAlarm.value;

			// if startDate from the control is in the past, set minStartDate to today's Date and minEndDate to next day
			this.minStartDate =
				new Date(deviceAccess.assignments[0].utcStartDate) < new Date()
					? new Date()
					: new Date(deviceAccess.assignments[0].utcStartDate);
			this.maxStartDate = new Date(
				deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
			);

			const todaysDate = new Date();
			todaysDate?.setDate(todaysDate?.getDate() + 1);
			this.minEndDate =
				new Date(deviceAccess.assignments[0].utcStartDate) < todaysDate
					? todaysDate
					: new Date(deviceAccess.assignments[0].utcStartDate);
			this.maxEndDate = new Date(
				deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
			);
		} else {
			// In edit mode, only set disabled states if the inherited control doesn't allow them
			const currentControl = this.edits.control;

			// Preserve current times but validate them
			selectedControl.startTime = currentControl.startTime;
			selectedControl.endTime = currentControl.endTime;

			// For days, only set disabled state, preserve current values
			selectedControl.days.monday = {
				disabled: !deviceAccess.control.days.monday.value,
				value: !deviceAccess.control.days.monday.value
					? false
					: currentControl.days.monday.value,
			};
			selectedControl.days.tuesday = {
				disabled: !deviceAccess.control.days.tuesday.value,
				value: !deviceAccess.control.days.tuesday.value
					? false
					: currentControl.days.tuesday.value,
			};
			selectedControl.days.wednesday = {
				disabled: !deviceAccess.control.days.wednesday.value,
				value: !deviceAccess.control.days.wednesday.value
					? false
					: currentControl.days.wednesday.value,
			};
			selectedControl.days.thursday = {
				disabled: !deviceAccess.control.days.thursday.value,
				value: !deviceAccess.control.days.thursday.value
					? false
					: currentControl.days.thursday.value,
			};
			selectedControl.days.friday = {
				disabled: !deviceAccess.control.days.friday.value,
				value: !deviceAccess.control.days.friday.value
					? false
					: currentControl.days.friday.value,
			};
			selectedControl.days.saturday = {
				disabled: !deviceAccess.control.days.saturday.value,
				value: !deviceAccess.control.days.saturday.value
					? false
					: currentControl.days.saturday.value,
			};
			selectedControl.days.sunday = {
				disabled: !deviceAccess.control.days.sunday.value,
				value: !deviceAccess.control.days.sunday.value
					? false
					: currentControl.days.sunday.value,
			};

			// For permissions, only set disabled state, preserve current values
			selectedControl.permissions.canInstallDevice = {
				disabled: !deviceAccess.control.permissions.canInstallDevice.value,
				value: !deviceAccess.control.permissions.canInstallDevice.value
					? false
					: currentControl.permissions.canInstallDevice.value,
			};
			selectedControl.permissions.canOpenKeyDoor = {
				disabled: !deviceAccess.control.permissions.canOpenKeyDoor.value,
				value: !deviceAccess.control.permissions.canOpenKeyDoor.value
					? false
					: currentControl.permissions.canOpenKeyDoor.value,
			};
			selectedControl.permissions.canRemoveDevice = {
				disabled: !deviceAccess.control.permissions.canRemoveDevice.value,
				value: !deviceAccess.control.permissions.canRemoveDevice.value
					? false
					: currentControl.permissions.canRemoveDevice.value,
			};
			selectedControl.permissions.canReleaseHasp = {
				disabled: !deviceAccess.control.permissions.canReleaseHasp.value,
				value: !deviceAccess.control.permissions.canReleaseHasp.value
					? false
					: currentControl.permissions.canReleaseHasp.value,
			};
			selectedControl.permissions.canArmDisarmAjaxAlarm = {
				disabled: !deviceAccess.control.permissions.canArmDisarmAjaxAlarm.value,
				value: !deviceAccess.control.permissions.canArmDisarmAjaxAlarm.value
					? false
					: currentControl.permissions.canArmDisarmAjaxAlarm.value,
			};
			selectedControl.permissions.canOverrideAjaxAlarm = {
				disabled: !deviceAccess.control.permissions.canOverrideAjaxAlarm.value,
				value: !deviceAccess.control.permissions.canOverrideAjaxAlarm.value
					? false
					: currentControl.permissions.canOverrideAjaxAlarm.value,
			};
			this.deviceAssignmentEdits.allowCodeGeneration = {
				disabled: !(deviceAccess?.assignments[0]?.allowCodeGeneration ?? true),
				value: !(deviceAccess?.assignments[0]?.allowCodeGeneration ?? true)
					? false
					: this.deviceAssignmentEdits.allowCodeGeneration.value,
			};
			this.deviceAssignmentEdits.allowHaspCodeGeneration = {
				disabled: !(
					deviceAccess?.assignments[0]?.allowHaspCodeGeneration ?? true
				),
				value: this.hasOnlyGen3WallMountDevices
					? false
					: !(deviceAccess?.assignments[0]?.allowHaspCodeGeneration ?? true)
						? false
						: this.deviceAssignmentEdits.allowHaspCodeGeneration.value,
			};

			// If editing, allow the user to select dates in the past, just make sure to follow the min/max boundaries if editing an inheriting assignment
			this.minStartDate = new Date(deviceAccess.assignments[0].utcStartDate);
			this.maxStartDate = new Date(
				deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
			);

			// Set minEndDate to whichever comes sooner: minStartDate or dayAfterStartDate
			const dayAfterStartDate =
				this.minStartDate < this.startDate!
					? new Date(this.minStartDate)
					: new Date(this.startDate!);
			dayAfterStartDate?.setDate(dayAfterStartDate?.getDate() + 1);
			this.minEndDate = dayAfterStartDate;
			this.maxEndDate = new Date(
				deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
			);
		}

		// Force revalidation by setting the current values again
		if (this.startDate) {
			this.startDateTimeControl.setValue(this.startDate.toISOString(), {
				emitEvent: false,
			});
		}
		if (this.endDate) {
			this.endDateTimeControl.setValue(this.endDate.toISOString(), {
				emitEvent: false,
			});
		}

		if (this.isEditMode) {
			// Trigger validation and mark fields as touched to show validation state
			this.startDateTimeControl.updateValueAndValidity({
				onlySelf: true,
				emitEvent: true,
			});
			this.endDateTimeControl.updateValueAndValidity({
				onlySelf: true,
				emitEvent: true,
			});
			this.startDateTimeControl.markAsTouched();
			this.endDateTimeControl.markAsTouched();
		}

		this.edits.control = selectedControl;
		this.deviceAssignmentEdits.allowCodeGeneration.disabled = !(
			deviceAccess?.assignments[0]?.allowCodeGeneration ?? true
		);
		this.deviceAssignmentEdits.allowHaspCodeGeneration.disabled = !(
			deviceAccess?.assignments[0]?.allowHaspCodeGeneration ?? true
		);
		this.showCodeGenerationOption =
			deviceAccess.assignments[0].allowCodeGeneration ?? false;
		this.selectedControlAssignment = deviceAccess.assignments[0];
		this.cdr.detectChanges();
	}

	onSelectedCompaniesChangedEvent(event: any): void {
		if (event === undefined) {
			return;
		}
		this.companyList = [...event];
	}

	onSelectedUsersChangedEvent(event: any): void {
		if (event === undefined) {
			return;
		}
		this.userList = [...event];
	}

	onSelectedDeviceGroupsChangedEvent(event: any): void {
		if (event === undefined) {
			return;
		}
		this.deviceGroupList = [...event];

		this.requestDeviceControlSummaries();
	}

	onSubauthorizationValueChanged(newValue: boolean | null): void {
		if (
			this.isEditMode &&
			this.assignmentType === 'company' &&
			this.previousSubauthorizationValue !== newValue
		) {
			if (newValue === false) {
				// Only show confirmation dialog if:
				// - There are multiple companies being edited, OR
				// - It's not switching from null to false (i.e., it was previously true), AND
				// - The original value was not false, AND
				// - There are downstream companies
				const isSingleCompany =
					this.data.controlAssignments &&
					this.data.controlAssignments.length === 1;
				const isSwitchingFromNullToFalse =
					this.previousSubauthorizationValue === null;
				const originalValueWasFalse =
					this.originalSubauthorizationValue === false;
				const hasDownstreamCompanies =
					this.descendantCompanies && this.descendantCompanies.length > 0;

				if (
					(!isSingleCompany || !isSwitchingFromNullToFalse) &&
					!originalValueWasFalse &&
					hasDownstreamCompanies
				) {
					this.showSubauthorizationConfirmationDialog();
				} else {
					// For cases where we don't need confirmation, just update the previous value
					this.previousSubauthorizationValue = newValue;
				}
			} else {
				// Update the previous value for next change
				this.previousSubauthorizationValue = newValue;
			}
		}
	}

	showSubauthorizationConfirmationDialog(): void {
		this.dialog
			.open(CancelConfirmationComponent, {
				panelClass: 'styled-dialog-container-warning-v2',
				maxWidth: '510px',
				data: {
					dialogHeader: $localize`:@@assignDeviceControl-subauthorizationWarningHeader:Remove Sub-Authorization?`,
					dialogContent: $localize`:@@assignDeviceControl-subauthorizationWarningContent:All downstream company access will be terminated if you remove sub-authorization. This action cannot be undone.`,
					dialogPrimaryButtonText: $localize`:@@assignDeviceControl-confirmYesRemove:Yes, Remove`,
					dialogSecondaryButtonText: $localize`:@@assignDeviceControl-cancelDisable:Cancel`,
					showDevider: true,
					useSmallButtons: true,
					useWarningStyle: true,
					useCompactStyle: true,
					primaryFunction: () => {
						// User confirmed, need to keep the "no" value
						this.previousSubauthorizationValue = false;
					},
					secondaryFunction: () => {
						// User cancelled, need to revert to previous value
						this.subauthorizationControl.setValue(
							this.previousSubauthorizationValue
						);
					},
				},
			})
			.afterClosed()
			.subscribe((result) => {
				// If dialog was closed without clicking primary or secondary buttons (e.g., backdrop click, ESC key)
				// revert the sub-authorization value to its previous state
				if (result === undefined) {
					this.subauthorizationControl.setValue(
						this.previousSubauthorizationValue
					);
				}
			});
	}

	openOrClosePanel(evt: any, trigger: MatAutocompleteTrigger): void {
		evt.stopPropagation();
		this.dcInputHasFocus = !this.dcInputHasFocus;
		if (trigger.panelOpen) {
			trigger.closePanel();
		} else {
			trigger.openPanel();
		}
	}

	getParentControlIdFromInheritanceChain(
		inheritanceChain: string
	): string | undefined {
		// Split the string by commas and remove empty strings
		const parts = inheritanceChain.split(',').filter((part) => part.length > 0);

		// If we have enough parts, get the second from last
		if (parts.length >= 2) {
			return parts[parts.length - 2];
		}
		return undefined;
	}

	getApiService() {
		return this.apiService;
	}

	async getInheritedDeviceGroupControlWithEditModeCheck(
		deviceGroupId: string,
		deviceId: string
	): Promise<ControlResponse[]> {
		// Check if we're in edit mode and have inheritance chain data
		if (
			this.isEditMode &&
			this.data.controlAssignments &&
			this.data.controlAssignments.length >= 1
		) {
			// Only pass child control IDs if we have multiple access groups (multiple parent controls)
			// For single company/user edits OR ones with the same parent control, we want to get
			// only their inherited control
			if (this.checkIfMultipleAccessGroups()) {
				const childControlIds = this.data.controlAssignments.map(
					(assignment) => assignment.id
				);
				if (childControlIds.length > 0) {
					// In edit mode with multiple access groups, pass the child control IDs
					const result =
						await this.apiService.getInheritedDeviceGroupControlById(
							deviceGroupId,
							deviceId,
							childControlIds
						);
					return result;
				}
			} else {
				return await this.apiService.getInheritedDeviceGroupControlById(
					deviceGroupId,
					deviceId,
					[this.data.controlAssignments[0].id]
				);
			}
		}

		// Default behavior - pass the original deviceId
		return await this.apiService.getInheritedDeviceGroupControlById(
			deviceGroupId,
			deviceId
		);
	}

	// New methods for multi-step edit mode
	checkIfMultipleAccessGroups(): boolean {
		if (
			!this.isEditMode ||
			!this.data.controlAssignments ||
			this.data.controlAssignments.length <= 1
		) {
			return false;
		}

		// Group assignments by their parent control assignment ID
		const parentControlGroups = new Map<string, ControlAssignment[]>();

		this.data.controlAssignments.forEach((assignment) => {
			if (assignment.controlAssignmentInheritanceChain) {
				const parentControlId = this.getParentControlIdFromInheritanceChain(
					assignment.controlAssignmentInheritanceChain
				);
				if (parentControlId) {
					if (!parentControlGroups.has(parentControlId)) {
						parentControlGroups.set(parentControlId, []);
					}
					parentControlGroups.get(parentControlId)!.push(assignment);
				}
			}
		});

		// If we have more than one parent control group, we need the selection step
		return parentControlGroups.size > 1;
	}

	async prepareAccessGroupSelectionStep(): Promise<boolean> {
		if (!this.checkIfMultipleAccessGroups()) {
			return false;
		}

		this.showAccessGroupSelectionStep = true;
		this.hadMultipleAccessGroups = true;

		// Get the parent control assignment groups from the API for the first step of the multi-step edit mode
		if (this.data.deviceGroupId || this.data.deviceId) {
			const deviceGroupId = this.data.deviceGroupId || this.data.deviceId!;
			const deviceId = this.data.deviceId || '0';

			try {
				const apiResponse =
					await this.getInheritedDeviceGroupControlWithEditModeCheck(
						deviceGroupId,
						deviceId
					);
				if (apiResponse && apiResponse.length > 1) {
					this.accessGroups = apiResponse;
					this.resultingDeviceAccess = apiResponse;
				}
			} catch (error) {
				this.banner.sendMessage(
					$localize`:@@assignDeviceControl-errorFetching:Error fetching access groups! Please try again later.`
				);
				// eslint-disable-next-line no-console
				console.error('Error fetching access groups:', error);
			}
		}

		// Auto-select the first access group by default
		if (this.accessGroups.length > 0) {
			this.onAccessGroupSelected(this.accessGroups[0]);
		}
		return true;
	}

	onAccessGroupSelected(accessGroup: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}): void {
		this.selectedAccessGroup = accessGroup;

		// The backend returns the parent control assignment, so we can use its ID directly
		// to find the child assignments that inherit from this parent
		const parentControlId = accessGroup.assignments[0].id;

		// Filter the original control assignments to get the ones that belong to this access group
		const matchingAssignments = this.originalControlAssignments.filter(
			(assignment) => {
				if (assignment.controlAssignmentInheritanceChain) {
					const assignmentParentControlId =
						this.getParentControlIdFromInheritanceChain(
							assignment.controlAssignmentInheritanceChain
						);
					return assignmentParentControlId === parentControlId;
				}
				return false;
			}
		);

		// De-duplicate by company/user ID to prevent showing the same company/user multiple times
		// just in case the API returns the same company/user multiple times
		const uniqueEntities = new Map<string, any>();

		if (this.assignmentType === 'company') {
			matchingAssignments.forEach((assignment) => {
				const entityId = assignment.entityRelationship?.childCompany?.id;
				if (entityId && !uniqueEntities.has(entityId)) {
					uniqueEntities.set(entityId, {
						childEntityName:
							assignment.entityRelationship?.childCompany?.name ||
							'Unknown Company',
					});
				}
			});
		} else {
			matchingAssignments.forEach((assignment) => {
				const entityId = assignment.entityRelationship?.childUser?.id;
				if (entityId && !uniqueEntities.has(entityId)) {
					uniqueEntities.set(entityId, {
						childEntityName:
							assignment.entityRelationship?.childUser?.firstName &&
							assignment.entityRelationship?.childUser?.lastName
								? `${assignment.entityRelationship.childUser.firstName} ${assignment.entityRelationship.childUser.lastName}`
								: 'Unknown User',
					});
				}
			});
		}

		this.affectedAssignments = Array.from(uniqueEntities.values());
	}

	onNextStepClicked(): void {
		if (!this.selectedAccessGroup) {
			return;
		}

		// Filter the control assignments to only include those from the selected parent control assignment group
		// The backend returns the parent control assignment, so we can use its ID directly
		const parentControlId = this.selectedAccessGroup.assignments[0].id;

		// Filter from original assignments to preserve duplicates
		const filteredAssignments = this.originalControlAssignments.filter(
			(assignment) => {
				if (assignment.controlAssignmentInheritanceChain) {
					const assignmentParentControlId =
						this.getParentControlIdFromInheritanceChain(
							assignment.controlAssignmentInheritanceChain
						);
					return assignmentParentControlId === parentControlId;
				}
				return false;
			}
		);

		// Hide the selection step and proceed to normal edit form
		this.showAccessGroupSelectionStep = false;

		// Populate the form for the filtered assignments in case there is only one assignment selected
		if (filteredAssignments?.length === 1) {
			this.populateFormForEdit(filteredAssignments[0]);
		}

		// Show only the bubble selected from the previous screen and auto-select it
		this.resultingDeviceAccess = [this.selectedAccessGroup];
		// Update the clonedDeviceControlsJSON to reflect the new resultingDeviceAccess (for edit mode selection and changes to the device control)
		this.clonedDeviceControlsJSON = JSON.stringify(
			[...this.resultingDeviceAccess].map((res) => res.control)
		);
		this.onSelectedInheritorChangedEvent(this.resultingDeviceAccess[0]);

		// Fetch descendant companies for the assignment's company (if available)
		// both for single and multiple access editing
		if (
			filteredAssignments &&
			filteredAssignments.length > 0 &&
			this.assignmentType === 'company'
		) {
			this.apiService
				.getDeviceControlDescendantCompanies(
					filteredAssignments.map((assignment) => assignment.id)
				)
				.then((descendantCompanies) => {
					this.descendantCompanies = descendantCompanies;
					this.cdr.detectChanges();
				})
				.catch((e) => {
					this.descendantCompanies = [];
					this.cdr.detectChanges();
				});
		}

		// Update the next step with the filtered assignments that the user has selected on the previous screen
		this.data.controlAssignments = filteredAssignments;

		this.cdr.detectChanges();
	}

	onCancelAccessGroupSelection(): void {
		this.dialogRef.close();
	}

	onBackToAccessGroupSelection(): void {
		// Show the access group selection step again
		this.showAccessGroupSelectionStep = true;

		// Reset the form state
		this.resultingDeviceAccess = this.accessGroups;

		// Update the clonedDeviceControlsJSON to reflect the access groups
		this.clonedDeviceControlsJSON = JSON.stringify(
			[...this.accessGroups].map((res) => res.control)
		);

		// Clear the form edits
		this.edits.control = emptyDeviceControl();
		this.deviceAssignmentEdits = emptyDeviceAssignment();
		this.startDate = undefined;
		this.endDate = undefined;
		this.startDateTimeControl.reset();
		this.endDateTimeControl.reset();
		this.subauthorizationControl.reset();

		// Clear descendant companies
		this.descendantCompanies = [];

		// Reset the control assignments to the original data
		this.data.controlAssignments = this.originalControlAssignments;

		// Trigger change detection
		this.cdr.detectChanges();
	}
}
